import _ from 'lodash';
import React from 'react';
import { withRouter } from 'found';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import styles from '../Document.scss';
import styleVariables from '../../variables';
import Header from './Header';
import MobileSidebar from 'react-sidebar';
import Sidebar from '../../App/SidebarComponent';
import NotFound from '../../App/404';
import { Loader } from '../../Elements';
import { GlobalNotifications, Intent } from '../../Modules';
import { Container, Grid, Rail, Segment } from 'semantic-ui-react';
import Actions from './Actions';
import Brand from './Brand';
import Logo from './Logo';
import Renderer from './Renderer/Renderer';
import Builder from './Builder/Builder';
import Controls from './Renderer/Controls';

const Form = class Form extends React.Component {
  static propTypes = {
    node: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    params: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.addContentBlock = this.addContentBlock.bind(this);
    this.addQuestion = this.addQuestion.bind(this);
    this.canEdit = this.canEdit.bind(this);
    this.onSetSidebarOpen = this.onSetSidebarOpen.bind(this);
    this.onResponseChange = this.onResponseChange.bind(this);
    this.generateId = this.generateId.bind(this);
    this.toggleSidebar = this.toggleSidebar.bind(this);
    this.deleteQuestion = this.deleteQuestion.bind(this);
    this.updateResponse = this.updateResponse.bind(this);
    this.updateForm = this.updateForm.bind(this);
    this.onChange = this.onChange.bind(this);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.allChangesSaved = this.allChangesSaved.bind(this);

    this.state = {
      error: !this.props.node,
      sidebarOpen: false
    };
  }

  componentWillMount() {
    this.updateWindowDimensions();
    if (this.props.location.query.share_token) {
      this.addFormToSession(() => this.initializeForm(this.props));
    } else if (this.props.node) {
      this.initializeForm(this.props);
    }
    if (this.props.params.action !== 'edit') {
      this.setState({
        dirtyChecker: setInterval(() => this.updateResponse(), 5000)
      });
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillReceiveProps(nextProps) {
    // Temporary fix for #304 until relay fixes callback handling
    // https://github.com/unp/cloverbook-rails/issues/304
    if (!this.props.node && nextProps.node) {
      this.initializeForm(nextProps);
    }
  }

  componentWillUnmount() {
    if (this.state.dirtyChecker) clearInterval(this.state.dirtyChecker);
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  onSetSidebarOpen(open) {
    this.setState({ sidebarOpen: open });
  }

  onChange({ schema, uiSchema, formData }, callback) {
    this.setState({ schema, uiSchema, formData }, callback);
  }

  onResponseChange(key, value) {
    this.setState({
      formData: Object.assign({}, this.state.formData, { [key]: value })
    });
  }

  generateId() {
    return `${Math.random().toString(36)}00000000000000000`.slice(2, 16 + 2);
  }

  allChangesSaved() {
    return this.props.node.data === JSON.stringify(this.state.formData);
  }

  addContentBlock() {
    const state = _.cloneDeep(this.state);
    const key = this.generateId();
    state.schema.properties[key] = { type: 'null', title: null };
    state.uiSchema['ui:order'].push(key);
    state.uiSchema[key] = { 'ui:widget': 'contentBlock', 'ui:content': '' };
    this.setState(state, () => this.updateForm());
  }

  addQuestion() {
    const state = _.cloneDeep(this.state);
    const key = this.generateId();
    state.schema.properties[key] = { type: 'string', title: null };
    state.uiSchema['ui:order'].push(key);
    state.uiSchema[key] = { 'ui:widget': 'text', 'ui:description': '' };
    this.setState(state, () => this.updateForm());
  }

  addFormToSession(onSuccess) {
    this.setState({ error: false });
    const onCompleted = () => {
      this.props.relay.refetch(
        fragmentVariables => fragmentVariables,
        null,
        onSuccess
      );
    };

    const onError = () => {
      this.setState({ error: true });
    };

    const mutation = graphql`
      mutation FormComponent_addFormToSessionMutation(
        $input: AddFormToSessionInput!
      ) {
        addFormToSession(input: $input) {
          form {
            id
          }
        }
      }
    `;

    const environment = this.props.relay.environment;
    const { share_token } = this.props.location.query;

    commitMutation(environment, {
      mutation,
      variables: { input: { share_token } },
      onCompleted,
      onError
    });
  }

  initializeForm(props) {
    const { node } = props;
    if (node) {
      this.setState({
        initialFormData: JSON.parse(node.data),
        formData: JSON.parse(node.data),
        schema: JSON.parse(node.schema),
        uiSchema: JSON.parse(node.ui_schema)
      });
    }
  }

  toggleSidebar(ev) {
    ev.preventDefault();
    this.onSetSidebarOpen(!this.state.sidebarOpen);
  }

  canEdit() {
    return this.props.node.can_edit && this.props.params.action === 'edit';
  }

  deleteQuestion(key) {
    const state = _.cloneDeep(this.state);
    delete state.schema.properties[key];
    delete state.formData[key];
    delete state.uiSchema[key];
    delete state.initialFormData[key];
    state.uiSchema['ui:order'].splice(
      state.uiSchema['ui:order'].indexOf(key),
      1
    );
    this.setState(state, () => this.updateForm());
  }

  updateForm(onSuccess, onFailure) {
    const onError = () => {
      if (onFailure) onFailure();
      GlobalNotifications.show({
        message: 'Unable to update form',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (onSuccess) onSuccess();
    };

    const { id } = this.props.node;

    const mutation = graphql`
      mutation FormComponent_UpdateFormMutation($input: UpdateFormInput!) {
        updateForm(input: $input) {
          form {
            schema
            ui_schema
          }
        }
      }
    `;

    const schema = JSON.stringify(this.state.schema);
    const ui_schema = JSON.stringify(this.state.uiSchema); // eslint-disable-line camelcase
    const data = JSON.stringify(this.state.formData);

    const optimisticResponse = {
      updateForm: {
        form: { id, schema, ui_schema, data }
      }
    };
    const environment = this.props.relay.environment;

    if (
      this.props.node.ui_schema !== JSON.stringify(this.state.uiSchema) ||
      this.props.node.schema !== JSON.stringify(this.state.schema)
    ) {
      commitMutation(environment, {
        mutation,
        variables: {
          input: {
            id,
            schema,
            ui_schema,
            data
          }
        },
        optimisticResponse,
        onCompleted,
        onError
      });
    } else {
      if (onSuccess) onSuccess();
    }
  }

  updateResponse(onSuccess, onFailure) {
    if (this.props.node && !this.allChangesSaved()) {
      const onError = () => {
        if (onFailure) onFailure();
        GlobalNotifications.show({
          message: 'Unable to save form',
          intent: Intent.DANGER
        });
      };
      const onCompleted = () => {
        if (onSuccess) onSuccess();
      };

      const { id } = this.props.node;

      const mutation = graphql`
        mutation FormComponent_FillFormMutation($input: FillFormInput!) {
          fillForm(input: $input) {
            form {
              data
            }
          }
        }
      `;

      const data = JSON.stringify(this.state.formData);

      const optimisticResponse = {
        fillForm: {
          form: { id, data }
        }
      };
      const environment = this.props.relay.environment;

      commitMutation(environment, {
        mutation,
        variables: {
          input: {
            id,
            data
          }
        },
        optimisticResponse,
        onCompleted,
        onError
      });
    }
  }

  updateWindowDimensions() {
    this.setState({
      window: { width: window.innerWidth, height: window.innerHeight }
    });
  }

  render() {
    const isLargeScreen = this.state.window.width >= 1280;

    const ActionsMenu = (
      <Actions
        node={this.props.node}
        params={this.props.params}
        relay={this.props.relay}
        router={this.props.router}
        windowDimensions={this.state.window}
      />
    );

    return (
      <div>
        {this.props.node &&
          <MobileSidebar
            sidebar={
              <Sidebar
                setSidebarOpenState={this.onSetSidebarOpen}
                router={this.props.router}
                viewer={this.props.viewer}
              />
            }
            sidebarClassName={styles.sidebar}
            open={this.state.sidebarOpen}
            onSetOpen={this.onSetSidebarOpen}
            touch={false}
            contentClassName={styles.mobileSidebarContent}
            rootClassName={styles.mobileSidebarRoot}
          >
            <Header
              allChangesSaved={this.allChangesSaved()}
              toggleSidebar={this.toggleSidebar}
              sidebarOpen={this.state.sidebarOpen}
              document={this.props.node}
              relay={this.props.relay}
              router={this.props.router}
              params={this.props.params}
              viewer={this.props.viewer}
              windowDimensions={this.state.window}
            />
            <Container className={styles.container}>
              {!isLargeScreen &&
                this.state.window.width >= styleVariables.tabletBreakpoint &&
                <Grid>
                  <Grid.Row
                    columns='equal'
                    className={styles.actionsMenuTablet}
                  >
                    <Grid.Column>
                      {ActionsMenu}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>}
              <Segment className={styles.segment}>
                {isLargeScreen &&
                  <Rail
                    position='right'
                    close
                    className={styles.actionsMenuDesktop}
                  >
                    {ActionsMenu}
                  </Rail>}
                <Grid padded stackable relaxed>
                  <Grid.Row columns='equal'>
                    <Grid.Column>
                      <Logo
                        canEdit={this.canEdit()}
                        viewer={this.props.viewer}
                        node={this.props.node}
                        relay={this.props.relay}
                      />
                      <Brand
                        canEdit={this.canEdit()}
                        viewer={this.props.viewer}
                        node={this.props.node}
                        relay={this.props.relay}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      {this.props.params.action !== 'edit' &&
                        <Controls
                          allChangesSaved={this.allChangesSaved()}
                          updateResponse={this.updateResponse}
                        />}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row columns='equal' style={{ marginTop: '2em' }}>
                    <Grid.Column>
                      {this.props.params.action === 'edit' &&
                        <Builder
                          addContentBlock={this.addContentBlock}
                          addQuestion={this.addQuestion}
                          schema={this.state.schema}
                          uiSchema={this.state.uiSchema}
                          onChange={this.onChange}
                          updateForm={this.updateForm}
                          deleteQuestion={this.deleteQuestion}
                          formData={this.state.formData}
                        />}
                      {this.props.params.action !== 'edit' &&
                        <Renderer
                          updateResponse={this.updateResponse}
                          allChangesSaved={this.allChangesSaved()}
                          formData={this.state.initialFormData}
                          onChange={this.onResponseChange}
                          schema={this.state.schema}
                          uiSchema={this.state.uiSchema}
                        />}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            </Container>
          </MobileSidebar>}
        {!this.props.node &&
          <div>
            {this.state.error && <NotFound viewer={this.props.viewer} />}
            {!this.state.error && <Loader />}
          </div>}
      </div>
    );
  }
};

export default withRouter(Form);



// WEBPACK FOOTER //
// ./webpack/components/Document/Form/FormComponent.js